import { Component, OnInit, inject, Pipe, PipeTransform } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DragDropModule, CdkDragEnd } from '@angular/cdk/drag-drop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocumentService } from '../../services/document.service';
import { ContactService } from '../../services/contact.service';
import { debounceTime, distinctUntilChanged, Subject, switchMap } from 'rxjs';

interface Signer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  signatureX?: number;
  signatureY?: number;
  signatureWidth?: number;
  signatureHeight?: number;
  signaturePage?: number;
  status?: string;
  require2FA?: boolean;
}

interface TextField {
  id: string;
  name: string;
  x: number;
  y: number;
  page: number;
  width: number;
  height: number;
  dragPosition: { x: number, y: number } | null;
}

@Pipe({
  name: 'safeUrl',
  standalone: true
})
export class SafeUrlPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}
  transform(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}

import { PdfViewerModule } from 'ng2-pdf-viewer';
import * as pdfjsLib from 'pdfjs-dist';

// Ensure the PDF worker loads correctly from a reliable CDN
(pdfjsLib as any).GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.mjs';

@Component({
  selector: 'app-editor',
  standalone: true,
  imports: [CommonModule, DragDropModule, FormsModule, PdfViewerModule],
  template: `
    <div class="editor-layout">
      
      <aside class="glass-sidebar left">
        <div class="sidebar-tabs">
          <button [class.active]="activeTab === 'signers'" (click)="activeTab = 'signers'">
            <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle></svg>
            Empfänger
          </button>
          <button *ngIf="isTemplate" [class.active]="activeTab === 'fields'" (click)="activeTab = 'fields'">
            <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
            Textfelder
          </button>
        </div>

        <!-- TAB: Signers -->
        <div class="tab-content" *ngIf="activeTab === 'signers'">
          <div class="sidebar-header-compact">
            <h3>Empfänger</h3>
            <button *ngIf="isTemplate" class="btn-quick-add" (click)="quickAddPlaceholder()">
              + Person hinzufügen
            </button>
          </div>

          <form *ngIf="!isTemplate" autocomplete="off" (submit)="$event.preventDefault()">
            <div class="add-form" [class.editing]="editingSignerId">
              <div class="autocomplete-container">
                <input [(ngModel)]="newSigner.name" name="signer_name" (input)="onSearchContacts($event)" placeholder="Name" class="modern-input">
                <div class="contact-dropdown" *ngIf="suggestedContacts.length > 0">
                  <div *ngFor="let contact of suggestedContacts" class="contact-item" (click)="selectContact(contact)">
                    <div class="contact-avatar">{{ contact.name.charAt(0).toUpperCase() }}</div>
                    <div class="contact-text"><strong>{{ contact.name }}</strong><small>{{ contact.email }}</small></div>
                  </div>
                </div>
              </div>
              <input [(ngModel)]="newSigner.email" name="signer_email" placeholder="E-Mail" type="email" class="modern-input">
              <div class="form-actions">
                <button class="btn-primary" type="button" (click)="saveSigner()" [disabled]="!newSigner.name || !newSigner.email">
                  {{ editingSignerId ? 'Aktualisieren' : 'Hinzufügen' }}
                </button>
              </div>
            </div>
          </form>

          <div class="signer-list scrollable">
            <div *ngFor="let signer of signers; let i = index" 
                 class="signer-card" 
                 [class.placed]="signer.signatureX !== null"
                 [style.border-left-color]="getPersonColor(i)"
                 draggable="true"
                 (dragstart)="onDragStartSidebar($event, signer)">
              <div class="avatar" [style.background]="getPersonColor(i)">{{ signer.name.charAt(signer.name.length-1) }}</div>
              <div class="signer-info">
                <strong>{{ signer.name }}</strong>
                <div class="id-row">
                  <small>ID: {{ signer.id.split('-')[0] }}...</small>
                  <button class="btn-copy-id" (click)="copyToClipboard($event, signer.id)" title="ID kopieren">
                    <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                  </button>
                </div>
              </div>
              <button class="btn-delete-signer" (click)="deleteSigner($event, signer.id)">
                <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2" fill="none"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              </button>
            </div>
          </div>
        </div>

        <!-- TAB: Text Fields -->
        <div class="tab-content" *ngIf="activeTab === 'fields' && isTemplate">
          <div class="sidebar-header-compact">
            <h3>Textfelder</h3>
          </div>
          <div class="add-form">
            <input [(ngModel)]="newTextFieldName" placeholder="Feld-Name (z.B. Firma)" class="modern-input">
            <button class="btn-primary" (click)="addTextField()" [disabled]="!newTextFieldName">
              Feld hinzufügen
            </button>
          </div>

          <div class="signer-list scrollable">
            <div *ngFor="let field of textFields" 
                 class="signer-card" 
                 [class.placed]="field.x !== null"
                 draggable="true"
                 (dragstart)="onDragStartField($event, field)">
              <div class="avatar-field">T</div>
              <div class="signer-info">
                <strong>{{ field.name }}</strong>
                <div class="id-row">
                  <small>ID: {{ field.id.split('-')[0] }}...</small>
                  <button class="btn-copy-id" (click)="copyToClipboard($event, field.id)" title="ID kopieren">
                    <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                  </button>
                </div>
              </div>
              <button class="btn-delete-signer" (click)="deleteTextField(field.id)">
                <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2" fill="none"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              </button>
            </div>
          </div>
        </div>
      </aside>

      <!-- CENTER: PDF VIEWER -->
      <main class="viewer-area">
        <div class="viewer-header">
          <div class="header-left">
            <h2 class="doc-title">{{ documentName }}</h2>
            <div class="doc-badges">
              <div class="doc-badge">PDF Dokument</div>
              <div *ngIf="isTemplate" class="doc-badge template-id" (click)="copyToClipboard($event, docId)" title="Template ID kopieren">
                <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none" style="margin-right: 4px;"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                <span>ID: {{ docId.split('-')[0] }}...</span>
                <span *ngIf="copyFeedback === docId" class="copy-hint">Kopiert!</span>
              </div>
            </div>
          </div>
          <button class="btn-download-doc" (click)="downloadPdf()" title="Originaldokument herunterladen">
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
            Download
          </button>
          <button class="btn-audit-doc" *ngIf="canShowAudit()" (click)="downloadAudit()" title="Audit-Zertifikat herunterladen">
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
            Audit
          </button>
        </div>
        
        <div class="canvas-wrapper">
          <div class="pdf-scroll-viewport">
            <div class="pdf-content-wrapper" *ngIf="pdfUrl" 
                 [style.height.px]="pdfHeight || 800"
                 (dragover)="onDragOverPDF($event)"
                 (drop)="onDropPDF($event)">
              <pdf-viewer 
                [src]="pdfUrl" 
                [render-text]="false"
                [original-size]="false"
                [show-all]="true"
                (after-load-complete)="onPdfLoaded($event)"
                class="pdf-render-element"
                style="display: block; width: 850px; height: 100%;">
              </pdf-viewer>
              
              <div class="dnd-overlay" style="height: 100%;">
                 <ng-container *ngFor="let signer of signers; let i = index">
                  <div *ngIf="signer.signatureX !== null && signer.signatureX !== undefined && signer.status !== 'signed'"
                       class="signature-box minimalist"
                       cdkDrag
                       [cdkDragBoundary]="'.pdf-content-wrapper'"
                       [cdkDragFreeDragPosition]="signer.dragPosition!"
                       (cdkDragEnded)="onDragEnded($event, signer)"
                       [style.width.px]="signer.signatureWidth || 200"
                       [style.height.px]="signer.signatureHeight || 60"
                       [style.border-color]="getPersonColor(i)">
                    
                    <button class="remove-sig-btn tiny" (mousedown)="$event.stopPropagation(); removeSignature($event, signer)">
                      <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>

                    <div class="sig-minimal-body">
                      <div class="person-tag" [style.background]="getPersonColor(i)">{{ signer.name }}</div>
                      <span class="placeholder-text">Unterschrift</span>
                    </div>
                    <div class="resize-handle tiny" (mousedown)="onResizeStart($event, signer)"></div>
                  </div>
                </ng-container>

                <ng-container *ngIf="isTemplate">
                  <ng-container *ngFor="let field of textFields">
                    <div *ngIf="field.x !== null && field.dragPosition"
                         class="field-box minimalist"
                         cdkDrag
                         [cdkDragBoundary]="'.pdf-content-wrapper'"
                         [cdkDragFreeDragPosition]="field.dragPosition!"
                         (cdkDragEnded)="onFieldDragEnded($event, field)"
                         [style.width.px]="field.width || 150"
                         [style.height.px]="field.height || 30">
                      
                      <button class="remove-sig-btn tiny" (mousedown)="$event.stopPropagation(); removeField($event, field)">
                        <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>

                      <div class="field-minimal-body">
                        <span class="field-label">{{ field.name }}</span>
                      </div>
                      <div class="resize-handle tiny" (mousedown)="onFieldResizeStart($event, field)"></div>
                    </div>
                  </ng-container>
                </ng-container>
              </div>
            </div>
          </div>
        </div>
      </main>

      <!-- RIGHT SIDEBAR: Actions -->
      <aside class="glass-sidebar right">
        <div class="action-panel">
          <div class="progress-indicator">
            <div class="progress-bar" [style.width]="(signers.length > 0 ? 100 : 0) + '%'"></div>
          </div>
          <h3>Aktionen</h3>
          <p class="instruction-text">Platzieren Sie die Unterschriftenfelder auf dem Dokument und klicken Sie auf Einladungen Senden.</p>
          
          <div class="btn-group">
            <button class="btn-secondary" (click)="goHome()">Abbrechen</button>
            
            <!-- Standard Contract: Send Invitations -->
            <button *ngIf="!isTemplate" class="btn-primary btn-glow" (click)="finish()" [disabled]="!canFinish() || isSending">
              <svg *ngIf="!isSending" viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M22 2L11 13"></path><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
              <span *ngIf="!isSending">Einladungen senden</span>
              <span *ngIf="isSending">Wird gesendet...</span>
            </button>

            <!-- Template: Save Configuration -->
            <button *ngIf="isTemplate" class="btn-primary btn-glow" style="background: linear-gradient(135deg, #a855f7, #6366f1);" (click)="saveTemplate()">
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
              <span>Vorlage speichern</span>
            </button>
            <div class="divider"></div>
            <button class="btn-hub" (click)="goToHub()">
              <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none"><polyline points="15 18 9 12 15 6"></polyline></svg>
              Hub
            </button>
          </div>
        </div>
      </aside>

    </div>
  `,
  styles: [`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Kalam:wght@400;700&display=swap');

    :host {
      font-family: 'Inter', sans-serif;
      display: block;
      height: 100vh;
      background-color: #0f172a;
      color: #f8fafc;
      overflow: hidden;
    }

    .editor-layout {
      display: grid;
      grid-template-columns: 320px 1fr 300px;
      height: 100vh;
      gap: 1.5rem;
      padding: 1.5rem;
    }

    .glass-sidebar {
      background: rgba(30, 41, 59, 0.7);
      backdrop-filter: blur(16px);
      border: 1px solid rgba(255,255,255,0.05);
      border-radius: 1.25rem;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .sidebar-header {
      padding: 1.5rem;
      border-bottom: 1px solid rgba(255,255,255,0.05);
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .header-icon {
      width: 32px; height: 32px;
      border-radius: 8px;
      background: rgba(56, 189, 248, 0.1);
      color: #38bdf8;
      display: flex; align-items: center; justify-content: center;
    }

    .sidebar-header h3 { margin: 0; font-size: 1.1rem; font-weight: 600; flex: 1; }
    
    .count-badge {
      background: #38bdf8; color: #0f172a; font-size: 0.75rem; font-weight: 700;
      padding: 0.1rem 0.5rem; border-radius: 999px;
    }

    .scrollable { flex: 1; overflow-y: auto; padding: 1.5rem; display: flex; flex-direction: column; gap: 0.75rem; background: rgba(15, 23, 42, 0.4); }
    .empty-list-hint { text-align: center; padding: 2rem; color: #64748b; font-size: 0.85rem; }

    .signer-card {
      background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.05);
      padding: 0.75rem; border-radius: 0.75rem; display: flex; align-items: center; gap: 0.75rem;
      transition: all 0.2s ease; cursor: pointer; position: relative;
    }
    .signer-card:hover { border-color: rgba(56, 189, 248, 0.3); background: rgba(56, 189, 248, 0.05); }
    .signer-card.active-edit { border-color: #38bdf8; background: rgba(56, 189, 248, 0.1); }

    .btn-delete-signer {
      background: transparent; border: none; color: #64748b; cursor: pointer; padding: 0.4rem;
      border-radius: 0.4rem; transition: all 0.2s; visibility: hidden;
    }
    .signer-card:hover .btn-delete-signer { visibility: visible; }
    .btn-delete-signer:hover { color: #f87171; background: rgba(248, 113, 113, 0.1); }

    .avatar {
      width: 36px; height: 36px; border-radius: 50%; background: linear-gradient(135deg, #818cf8, #c084fc);
      display: flex; align-items: center; justify-content: center; font-weight: 700; color: white;
    }

    .signer-info { flex: 1; display: flex; flex-direction: column; }
    .signer-info strong { font-size: 0.9rem; font-weight: 600; }
    .signer-info small { font-size: 0.75rem; color: #94a3b8; }
    
    .status-dot { width: 8px; height: 8px; border-radius: 50%; background: #fbbf24; }

    .add-form {
      padding: 1.5rem; border-bottom: 1px solid rgba(255,255,255,0.05); background: rgba(15, 23, 42, 0.2);
      display: flex; flex-direction: column; gap: 0.75rem; transition: background 0.3s;
    }
    .add-form.editing { background: rgba(56, 189, 248, 0.05); }
    .add-form h4 { margin: 0 0 0.5rem; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; }

    .modern-input {
      width: 100%; box-sizing: border-box; padding: 0.75rem 1rem; border-radius: 0.5rem;
      background: rgba(15, 23, 42, 0.8); border: 1px solid rgba(255,255,255,0.1);
      color: white; font-family: 'Inter', sans-serif; font-size: 0.875rem;
      transition: border-color 0.2s ease;
    }
    .modern-input:focus { outline: none; border-color: #38bdf8; }

    .form-actions { display: flex; gap: 0.5rem; }
    .btn-cancel-edit { background: transparent; border: none; color: #94a3b8; cursor: pointer; font-size: 0.85rem; font-weight: 500; }
    .btn-cancel-edit:hover { color: #f1f5f9; }

    .btn-primary {
      flex: 1; padding: 0.75rem; border-radius: 0.5rem; border: none;
      background: linear-gradient(135deg, #0ea5e9, #4f46e5); color: white;
      font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 0.5rem;
      cursor: pointer; transition: all 0.2s ease;
    }
    .btn-primary:hover:not([disabled]) { opacity: 0.9; transform: translateY(-1px); }
    .btn-primary[disabled] { opacity: 0.5; cursor: not-allowed; filter: grayscale(1); }

    .viewer-area {
      display: flex; flex-direction: column; border-radius: 1.25rem; overflow: hidden;
      background: #1e293b; border: 1px solid rgba(255,255,255,0.05);
    }

    .viewer-header {
      padding: 1rem 2rem; background: rgba(15, 23, 42, 0.7); border-bottom: 1px solid rgba(255,255,255,0.08);
      display: flex; justify-content: space-between; align-items: center;
    }
    .header-left { display: flex; align-items: center; gap: 1.25rem; }
    .doc-badges { display: flex; gap: 0.5rem; align-items: center; }
    .doc-title { margin: 0; font-size: 1.1rem; font-weight: 600; color: #f1f5f9; }
    .doc-badge { font-size: 0.7rem; padding: 0.2rem 0.6rem; background: rgba(56, 189, 248, 0.1); color: #38bdf8; border-radius: 999px; border: 1px solid rgba(56, 189, 248, 0.2); }
    .doc-badge.template-id {
      position: relative;
      background: rgba(168, 85, 247, 0.1); color: #a855f7; border-color: rgba(168, 85, 247, 0.2);
      cursor: pointer; transition: all 0.2s; pointer-events: auto;
    }
    .copy-hint {
      position: absolute; top: -25px; left: 50%; transform: translateX(-50%);
      background: #10b981; color: white; padding: 2px 8px; border-radius: 4px;
      font-size: 0.65rem; font-weight: 700; white-space: nowrap; animation: fadeInOut 1.5s forwards;
    }
    @keyframes fadeInOut {
      0% { opacity: 0; transform: translate(-50%, 0); }
      20% { opacity: 1; transform: translate(-50%, -5px); }
      80% { opacity: 1; transform: translate(-50%, -5px); }
      100% { opacity: 0; transform: translate(-50%, -10px); }
    }
    .doc-badge.template-id:hover { background: rgba(168, 85, 247, 0.2); border-color: #a855f7; }

    .btn-download-doc {
      display: flex; align-items: center; gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 0.6rem;
      color: #94a3b8;
      font-size: 0.85rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }
    .btn-download-doc:hover { background: #38bdf8; border-color: #38bdf8; color: #0f172a; }
    
    .btn-audit-doc {
      display: flex; align-items: center; gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: rgba(56, 189, 248, 0.1);
      border: 1px solid rgba(56, 189, 248, 0.2);
      border-radius: 0.6rem;
      color: #38bdf8;
      font-size: 0.85rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }
    .btn-audit-doc:hover { background: #38bdf8; color: #0f172a; }

    .canvas-wrapper {
      flex: 1; position: relative; background: #334155; overflow: hidden;
      display: flex; flex-direction: column;
    }

    .pdf-scroll-viewport {
      flex: 1; overflow: auto; padding: 3rem 1rem;
      display: flex; justify-content: center;
    }

    .pdf-content-wrapper {
      position: relative; 
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
      background: white;
      height: fit-content;
    }

    .pdf-render-element {
      display: block;
      min-height: 800px;
    }

    .dnd-overlay {
      position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 10; pointer-events: none;
    }

    .signature-box {
      position: absolute; min-width: 160px; min-height: 70px; 
      background: rgba(255, 255, 255, 0.95); 
      backdrop-filter: blur(10px);
      border: 1.5px solid rgba(14, 165, 233, 0.4);
      border-radius: 12px; 
      box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0,0,0,0.02);
      pointer-events: auto; cursor: grab;
      overflow: hidden; transition: box-shadow 0.2s ease, border-color 0.2s ease;
      display: flex; flex-direction: column;
    }
    .signature-box:hover {
      border-color: #0ea5e9;
      box-shadow: 0 15px 35px -5px rgba(14, 165, 233, 0.15), 0 0 0 1px rgba(14, 165, 233, 0.1);
    }
    .signature-box:active { cursor: grabbing; box-shadow: 0 5px 15px rgba(0,0,0,0.08); }
    
    .remove-sig-btn {
      position: absolute; top: 6px; right: 6px; width: 22px; height: 22px;
      border-radius: 50%; background: #f1f5f9; color: #94a3b8; border: none;
      display: flex; align-items: center; justify-content: center; cursor: pointer;
      transition: all 0.2s; z-index: 10; padding: 0;
    }
    .remove-sig-btn:hover { background: #fee2e2; color: #ef4444; }

    .sig-header { 
      padding: 8px 12px 0;
      pointer-events: none;
    }
    .sig-badge {
      display: inline-block;
      background: #e0f2fe; color: #0284c7; font-size: 0.6rem; font-weight: 700; 
      text-transform: uppercase; padding: 2px 6px; border-radius: 4px; letter-spacing: 0.05em; 
    }
    .sig-body { 
      flex: 1; display: flex; flex-direction: column; 
      align-items: center; justify-content: center; text-align: center; pointer-events: none;
      padding: 0 12px 8px; width: 100%; box-sizing: border-box;
    }
    .sig-name { 
      font-family: 'Kalam', cursive; color: #0f172a; font-weight: 700; 
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; line-height: 1.1;
    }
    .sig-email {
      font-family: 'Inter', sans-serif; color: #64748b;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; line-height: 1.2;
    }

    .field-box {
      border-color: rgba(168, 85, 247, 0.4);
    }
    .field-box:hover {
      border-color: #a855f7;
      box-shadow: 0 15px 35px -5px rgba(168, 85, 247, 0.15), 0 0 0 1px rgba(168, 85, 247, 0.1);
    }
    .field-name {
      font-size: 0.85rem;
      font-weight: 600;
      color: #1e293b;
    }

    /* Minimalist Styles */
    .signature-box.minimalist, .field-box.minimalist {
      position: absolute;
      left: 0;
      top: 0;
      background: rgba(255, 255, 255, 0.4);
      backdrop-filter: blur(4px);
      border: 1px dashed rgba(0,0,0,0.2);
      border-radius: 4px;
      box-shadow: none;
      pointer-events: auto;
      cursor: grab;
    }
    .signature-box.minimalist:hover, .field-box.minimalist:hover {
      background: rgba(255, 255, 255, 0.8);
      border-style: solid;
      border-color: inherit;
    }
    .sig-minimal-body, .field-minimal-body {
      padding: 4px; display: flex; flex-direction: column; height: 100%;
    }
    .person-tag {
      font-size: 0.65rem; font-weight: 800; color: white; padding: 2px 6px; border-radius: 2px; width: fit-content; margin-bottom: 2px;
    }
    .placeholder-text { font-size: 0.8rem; color: #475569; font-style: italic; }
    .field-label { font-size: 0.85rem; font-weight: 600; color: #1e293b; }

    .sidebar-tabs { display: flex; border-bottom: 1px solid rgba(255,255,255,0.05); }
    .sidebar-tabs button {
      flex: 1; padding: 1rem; background: transparent; border: none; color: #64748b;
      display: flex; align-items: center; justify-content: center; gap: 0.5rem;
      font-weight: 600; font-size: 0.9rem; cursor: pointer; transition: all 0.2s;
    }
    .sidebar-tabs button:hover { color: #f8fafc; background: rgba(255,255,255,0.02); }
    .sidebar-tabs button.active { color: #38bdf8; border-bottom: 2px solid #38bdf8; background: rgba(56, 189, 248, 0.05); }

    .tab-content { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
    .sidebar-header-compact { padding: 1.25rem 1.5rem 0.75rem; display: flex; justify-content: space-between; align-items: center; }
    .sidebar-header-compact h3 { margin: 0; font-size: 1rem; color: #f1f5f9; }
    
    .btn-quick-add {
      background: rgba(56, 189, 248, 0.1); border: 1px solid rgba(56, 189, 248, 0.2);
      color: #38bdf8; font-size: 0.75rem; font-weight: 600; padding: 0.25rem 0.75rem; border-radius: 0.5rem; cursor: pointer;
    }
    .btn-quick-add:hover { background: #38bdf8; color: #0f172a; }

    .avatar-field { width: 32px; height: 32px; border-radius: 6px; background: #6366f1; color: white; display: flex; align-items: center; justify-content: center; font-weight: 700; }
    .remove-sig-btn.tiny { width: 18px; height: 18px; top: -9px; right: -9px; background: #ef4444; color: white; }
    .resize-handle.tiny { width: 12px; height: 12px; border-right: 2px solid rgba(0,0,0,0.2); border-bottom: 2px solid rgba(0,0,0,0.2); bottom: 2px; right: 2px; cursor: nwse-resize; }

    .id-row { display: flex; align-items: center; gap: 0.25rem; }
    .btn-copy-id { background: transparent; border: none; color: #64748b; cursor: pointer; padding: 2px; border-radius: 2px; display: flex; align-items: center; }
    .btn-copy-id:hover { color: #38bdf8; background: rgba(56, 189, 248, 0.1); }

    .resize-handle {
      position: absolute; right: 2px; bottom: 2px; width: 20px; height: 20px; 
      cursor: nwse-resize; color: #cbd5e1;
      display: flex; align-items: center; justify-content: center;
      transition: color 0.2s ease;
    }
    .signature-box:hover .resize-handle { color: #0ea5e9; }

    .action-panel { padding: 1.5rem; }
    .progress-indicator { height: 4px; background: rgba(255,255,255,0.1); border-radius: 2px; margin-bottom: 1.5rem; overflow: hidden; }
    .progress-bar { height: 100%; background: #34d399; transition: width 0.3s ease; }
    
    .action-panel h3 { margin: 0 0 0.75rem; font-size: 1.1rem; }
    .instruction-text { color: #94a3b8; font-size: 0.875rem; line-height: 1.5; margin-bottom: 2rem; }

    .btn-group { display: flex; flex-direction: column; gap: 0.75rem; }
    
    .btn-secondary { background: transparent; border: 1px solid rgba(255,255,255,0.1); color: #94a3b8; padding: 0.75rem 1.25rem; border-radius: 0.5rem; font-weight: 600; cursor: pointer; transition: all 0.2s; }
    .btn-secondary:hover { color: white; border-color: white; }

    .divider { width: 1px; height: 24px; background: rgba(255,255,255,0.1); margin: 0 0.5rem; }
    
    .btn-hub {
      background: transparent; border: none; color: #64748b; font-weight: 600; cursor: pointer; 
      display: flex; align-items: center; gap: 0.25rem; transition: color 0.2s;
    }
    .btn-hub:hover { color: #38bdf8; }

    .btn-glow {
      box-shadow: 0 0 20px rgba(14, 165, 233, 0.4);
    }
    .btn-glow:hover:not([disabled]) { box-shadow: 0 0 30px rgba(14, 165, 233, 0.6); }

    .autocomplete-container { position: relative; }
    .contact-dropdown {
      position: absolute; top: 100%; left: 0; right: 0; z-index: 50;
      background: #1e293b; border: 1px solid rgba(255,255,255,0.1);
      border-radius: 0.5rem; margin-top: 0.25rem; overflow: hidden;
      box-shadow: 0 10px 15px -3px rgba(0,0,0,0.5);
    }
    .contact-item {
      padding: 0.75rem 1rem; display: flex; align-items: center; gap: 0.75rem;
      cursor: pointer; transition: background 0.2s;
    }
    .contact-item:hover { background: rgba(255,255,255,0.05); }
    .contact-avatar {
      width: 28px; height: 28px; border-radius: 50%; background: #475569;
      display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: 700;
    }
    .contact-text { display: flex; flex-direction: column; }
    .contact-text strong { font-size: 0.85rem; }
    .contact-text small { font-size: 0.7rem; color: #94a3b8; }

    .toggle-group {
      display: flex; align-items: center; gap: 0.75rem; padding: 0.5rem 0; color: #94a3b8; font-size: 0.85rem;
    }

    .switch { position: relative; display: inline-block; width: 34px; height: 20px; }
    .switch input { opacity: 0; width: 0; height: 0; }
    .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #475569; transition: .4s; }
    .slider:before { position: absolute; content: ""; height: 14px; width: 14px; left: 3px; bottom: 3px; background-color: white; transition: .4s; }
    input:checked + .slider { background-color: #38bdf8; }
    input:checked + .slider:before { transform: translateX(14px); }
    .slider.round { border-radius: 20px; }
    .slider.round:before { border-radius: 50%; }
  `]
})
export class EditorComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private documentService = inject(DocumentService);
  private contactService = inject(ContactService);

  docId: string = '';
  documentName: string = '';
  pdfUrl: string = '';
  pdfHeight: number = 0;
  signers: (Signer & { dragPosition: { x: number, y: number } | null })[] = [];
  newSigner = { name: '', email: '', phone: '', require2FA: false };
  editingSignerId: string | null = null;
  isSending = false;
  copyFeedback: string | null = null;
  isTemplate = false;
  activeTab: 'signers' | 'fields' = 'signers';
  personColors = ['#38bdf8', '#a855f7', '#f43f5e', '#fbbf24', '#10b981', '#6366f1'];

  textFields: TextField[] = [];
  newTextFieldName: string = '';

  suggestedContacts: any[] = [];
  private searchSubject = new Subject<string>();

  ngOnInit() {
    this.docId = this.route.snapshot.paramMap.get('id') || '';
    if (this.docId) {
      this.loadDocument();
    }

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => this.contactService.search(query))
    ).subscribe(contacts => {
      this.suggestedContacts = contacts;
    });
  }

  loadDocument() {
    this.documentService.getDocument(this.docId).subscribe(doc => {
      this.documentName = doc.originalName;
      this.isTemplate = doc.isTemplate;
      this.pdfUrl = this.documentService.getPdfUrl(doc.id);
      this.textFields = doc.textFields || [];
      this.signers = (doc.signers || []).map((s: Signer) => ({
        ...s,
        dragPosition: s.signatureX != null ? { x: s.signatureX, y: s.signatureY! } : null
      }));
      this.textFields = (doc.textFields || []).map((f: any) => ({
        ...f,
        dragPosition: f.x != null ? { x: f.x, y: f.y } : null
      }));
      
      this.documentService.getPdfBlob(this.docId).subscribe(blob => {
        if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = URL.createObjectURL(blob);
      });
    });
  }

  onPdfLoaded(pdfData: any) {
    setTimeout(() => {
      const pdfViewerEl = document.querySelector('.pdfViewer');
      if (pdfViewerEl) {
        this.pdfHeight = pdfViewerEl.scrollHeight;
      }

      const pages = document.querySelectorAll('.page');
      if (pages.length > 0) {
        this.signers.forEach(signer => {
          if (signer.signatureX != null && signer.signatureY != null && signer.signaturePage != null) {
            const pageIndex = signer.signaturePage || 0;
            const pageEl = pages[pageIndex < pages.length ? pageIndex : 0] as HTMLElement;
            if (pageEl) {
              signer.dragPosition = {
                x: signer.signatureX! + pageEl.offsetLeft,
                y: signer.signatureY! + pageEl.offsetTop
              };
            }
          }
        });

        this.textFields.forEach(field => {
          if (field.x != null && field.y != null && field.page != null) {
            const pageIndex = field.page || 0;
            const pageEl = pages[pageIndex < pages.length ? pageIndex : 0] as HTMLElement;
            if (pageEl) {
              field.dragPosition = {
                x: field.x + pageEl.offsetLeft,
                y: field.y + pageEl.offsetTop
              };
            }
          }
        });
      }
    }, 500);
  }

  onSearchContacts(event: any) {
    const query = event.target.value;
    if (query.length > 1) {
      this.searchSubject.next(query);
    } else {
      this.suggestedContacts = [];
    }
  }

  selectContact(contact: any) {
    this.newSigner.name = contact.name;
    this.newSigner.email = contact.email;
    this.newSigner.phone = contact.phone || '';
    this.suggestedContacts = [];
  }

  editSigner(signer: Signer) {
    this.editingSignerId = signer.id;
    this.newSigner = { 
      name: signer.name, 
      email: signer.email, 
      phone: signer.phone || '', 
      require2FA: !!signer.require2FA 
    };
  }

  cancelEdit() {
    this.editingSignerId = null;
    this.newSigner = { name: '', email: '', phone: '', require2FA: false };
  }

  quickAddPlaceholder() {
    const nextChar = String.fromCharCode(65 + this.signers.length); // A, B, C...
    const placeholder = {
      name: `Person ${nextChar}`,
      email: '',
      phone: '',
      require2FA: false
    };
    this.documentService.addSigner(this.docId, placeholder).subscribe(signer => {
      this.signers.push({
        ...signer,
        dragPosition: null
      });
    });
  }

  getPersonColor(index: number): string {
    return this.personColors[index % this.personColors.length];
  }

  saveSigner() {
    if (this.editingSignerId) {
      this.documentService.updateSigner(this.editingSignerId, this.newSigner).subscribe({
        next: (updatedSigner) => {
          const index = this.signers.findIndex(s => s.id === this.editingSignerId);
          if (index !== -1) {
            // Keep dragPosition
            const dragPos = this.signers[index].dragPosition;
            this.signers[index] = { ...updatedSigner, dragPosition: dragPos };
          }
          this.cancelEdit();
        },
        error: () => alert('Fehler beim Aktualisieren des Empfängers.')
      });
    } else {
      this.documentService.addSigner(this.docId, this.newSigner).subscribe(signer => {
        const newS = { ...signer, dragPosition: null };
        this.signers.push(newS);
        this.newSigner = { name: '', email: '', phone: '', require2FA: false };
      });
    }
  }

  deleteSigner(event: Event, signerId: string) {
    event.stopPropagation();
    if (confirm('Möchten Sie diesen Empfänger wirklich entfernen?')) {
      this.documentService.deleteSigner(signerId).subscribe({
        next: () => {
          this.signers = this.signers.filter(s => s.id !== signerId);
          if (this.editingSignerId === signerId) this.cancelEdit();
        },
        error: () => alert('Fehler beim Löschen des Empfängers.')
      });
    }
  }

  onDragStartSidebar(event: DragEvent, signer: any) {
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', signer.id);
      event.dataTransfer.effectAllowed = 'copy';
    }
  }

  onDragOverPDF(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onDropPDF(event: DragEvent) {
    event.preventDefault();
    const dataStr = event.dataTransfer?.getData('application/json');
    const signerId = event.dataTransfer?.getData('text/plain');

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const position = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };

    if (dataStr) {
      try {
        const data = JSON.parse(dataStr);
        if (data.type === 'field') {
          const field = this.textFields.find(f => f.id === data.id);
          if (field) {
            this.placeFieldAtPosition(field, { x: position.x - 90, y: position.y - 20 });
          }
          return;
        }
      } catch(e) {}
    } 
    
    if (signerId) {
      const signer = this.signers.find(s => s.id === signerId);
      if (signer) {
        this.placeSignerAtPosition(signer, { x: position.x - 110, y: position.y - 40 });
      }
    }
  }

  private isResizing = false;
  private resizeSigner: any = null;
  private startX = 0;
  private startY = 0;
  private startWidth = 0;
  private startHeight = 0;

  onResizeStart(event: MouseEvent, signer: any) {
    event.preventDefault();
    event.stopPropagation();
    this.isResizing = true;
    this.resizeSigner = signer;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startWidth = signer.signatureWidth || 220;
    this.startHeight = signer.signatureHeight || 80;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isResizing) return;
      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;
      this.resizeSigner.signatureWidth = Math.max(150, this.startWidth + dx);
      this.resizeSigner.signatureHeight = Math.max(60, this.startHeight + dy);
    };

    const onMouseUp = () => {
      this.isResizing = false;
      this.updateSigner(this.resizeSigner);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  onDragEnded(event: CdkDragEnd, signer: any) {
    if (this.isResizing) return;
    const position = event.source.getFreeDragPosition();
    this.placeSignerAtPosition(signer, position);
  }

  placeSignerAtPosition(signer: any, position: { x: number, y: number }) {
    const pages = document.querySelectorAll('.page');
    let targetPage = 0;
    let pageRelativeY = position.y;
    let pageRelativeX = position.x;

    for (let i = 0; i < pages.length; i++) {
      const pageEl = pages[i] as HTMLElement;
      const pageTop = pageEl.offsetTop;
      const pageBottom = pageTop + pageEl.offsetHeight;
      const boxCenterY = position.y + (signer.signatureHeight || 80) / 2; 

      if (boxCenterY >= pageTop && boxCenterY <= pageBottom) {
        targetPage = i;
        pageRelativeY = position.y - pageTop;
        pageRelativeX = position.x - pageEl.offsetLeft;
        break;
      }
    }

    signer.dragPosition = position; 
    signer.signatureX = pageRelativeX;
    signer.signatureY = pageRelativeY;
    signer.signaturePage = targetPage;
    
    if (!signer.signatureWidth) signer.signatureWidth = 220;
    if (!signer.signatureHeight) signer.signatureHeight = 80;

    this.updateSigner(signer);
  }

  updateSigner(signer: any) {
    this.documentService.updateSignerPosition(signer.id, { 
      x: signer.signatureX, 
      y: signer.signatureY, 
      page: signer.signaturePage,
      width: signer.signatureWidth,
      height: signer.signatureHeight
    }).subscribe();
  }

  removeSignature(event: Event, signer: any) {
    event.preventDefault();
    event.stopPropagation();
    signer.signatureX = null;
    signer.signatureY = null;
    signer.signaturePage = null;
    signer.dragPosition = null;
    this.updateSigner(signer);
  }

  canFinish() {
    return this.signers.length > 0 && this.signers.every(s => s.signatureX !== undefined && s.signatureX !== null);
  }

  finish() {
    this.isSending = true;
    this.documentService.sendInvitations(this.docId).subscribe({
      next: () => {
        alert('Einladungen wurden erfolgreich versendet!');
        this.router.navigate(['/pdf-signer']);
      },
      error: () => {
        alert('Fehler beim Senden der Einladungen');
        this.isSending = false;
      }
    });
  }

  copyToClipboard(event: Event, text: string) {
    event.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      this.copyFeedback = text;
      setTimeout(() => this.copyFeedback = null, 1500);
    });
  }

  saveTemplate() {
    // Everything is usually saved auto on drop, but we call saveTextFields to be sure
    this.saveTextFields();
    this.router.navigate(['/pdf-signer']);
  }

  goHome() {
    this.router.navigate(['/pdf-signer']);
  }

  goToHub() {
    this.router.navigate(['/launcher']);
  }

  // --- TEXT FIELDS LOGIC ---

  addTextField() {
    if (!this.newTextFieldName) return;
    const newField: TextField = {
      id: crypto.randomUUID(),
      name: this.newTextFieldName,
      x: 0,
      y: 0,
      page: 0,
      width: 180,
      height: 40,
      dragPosition: null
    };
    this.textFields.push(newField);
    this.newTextFieldName = '';
    this.saveTextFields();
  }

  deleteTextField(id: string) {
    this.textFields = this.textFields.filter(f => f.id !== id);
    this.saveTextFields();
  }

  onDragStartField(event: DragEvent, field: TextField) {
    if (event.dataTransfer) {
      event.dataTransfer.setData('application/json', JSON.stringify({ type: 'field', id: field.id }));
      event.dataTransfer.effectAllowed = 'copy';
    }
  }

  onFieldDragEnded(event: CdkDragEnd, field: TextField) {
    if (this.isResizingField) return;
    const position = event.source.getFreeDragPosition();
    this.placeFieldAtPosition(field, position);
  }

  placeFieldAtPosition(field: TextField, position: { x: number, y: number }) {
    const pages = document.querySelectorAll('.page');
    let targetPage = 0;
    let pageRelativeY = position.y;
    let pageRelativeX = position.x;

    for (let i = 0; i < pages.length; i++) {
      const pageEl = pages[i] as HTMLElement;
      const pageTop = pageEl.offsetTop;
      const pageBottom = pageTop + pageEl.offsetHeight;
      const boxCenterY = position.y + (field.height || 40) / 2;

      if (boxCenterY >= pageTop && boxCenterY <= pageBottom) {
        targetPage = i;
        pageRelativeY = position.y - pageTop;
        pageRelativeX = position.x - pageEl.offsetLeft;
        break;
      }
    }

    field.dragPosition = position;
    field.x = pageRelativeX;
    field.y = pageRelativeY;
    field.page = targetPage;
    this.saveTextFields();
  }

  removeField(event: Event, field: TextField) {
    event.preventDefault();
    event.stopPropagation();
    field.x = 0;
    field.y = 0;
    field.dragPosition = null;
    this.saveTextFields();
  }

  private isResizingField = false;
  private resizeField: TextField | null = null;
  
  onFieldResizeStart(event: MouseEvent, field: TextField) {
    event.preventDefault();
    event.stopPropagation();
    this.isResizingField = true;
    this.resizeField = field;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startWidth = field.width || 180;
    this.startHeight = field.height || 40;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isResizingField || !this.resizeField) return;
      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;
      this.resizeField.width = Math.max(100, this.startWidth + dx);
      this.resizeField.height = Math.max(30, this.startHeight + dy);
    };

    const onMouseUp = () => {
      this.isResizingField = false;
      this.saveTextFields();
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  saveTextFields() {
    const fieldsToSave = this.textFields.map(f => {
      const { dragPosition, ...rest } = f;
      return rest;
    });
    this.documentService.updateTextFields(this.docId, fieldsToSave).subscribe();
  }

  downloadPdf() {
    if (!this.docId) return;
    this.documentService.getPdfBlob(this.docId).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.documentName.endsWith('.pdf') ? this.documentName : this.documentName + '.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  }

  canShowAudit() {
    return this.signers.some(s => s.status === 'signed');
  }

  downloadAudit() {
    if (!this.docId) return;
    this.documentService.getAuditBlob(this.docId).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Audit-Zertifikat-${this.documentName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  }
}
